import type { QueryClient, QueryKey } from 'react-query';

import { useApiQuery } from '../../api/useApi';
import { useStateContext } from '../../contexts/ContextProvider';
import { usePermissions } from '../../contexts/PermissionContext';

/**
 * Le compteur de conversations non lues — badge de navigation et icône d'app.
 *
 * `GET /unread-conversations-count` (`CustomerController::getUnreadConversationsCount`,
 * gardé côté serveur par `permission:messaging.view`) répond
 * `{ unread_conversations_count }`, déjà borné au bon scope : l'établissement
 * sélectionné, ou tout le groupe et ses établissements liés quand la sélection en
 * est un — la même règle que la liste (#82), lue sur le même `facility_id`.
 *
 * **Gardé côté client aussi**, et pas seulement laissé au 403 serveur : afficher
 * une pastille à quelqu'un qui n'a pas `messaging.view` annoncerait une messagerie
 * qu'il n'a le droit de voir nulle part — même raison que #17 §5 pour les écrans.
 */

export interface NombreConversationsNonLues {
    unread_conversations_count: number;
}

/** `facilityId === null` : rien à demander, comme `urlConversations` (#27). */
export function urlNombreConversationsNonLues(facilityId: number | null): string {
    if (facilityId === null) return '';
    return `/unread-conversations-count?facility_id=${facilityId}`;
}

/** Même idiome que `estRequeteConversations` : un seul prédicat, réutilisé par l'invalidation. */
export function estRequeteNombreConversationsNonLues(query: { queryKey: QueryKey }): boolean {
    const cle = Array.isArray(query.queryKey) ? query.queryKey[0] : query.queryKey;
    return typeof cle === 'string' && cle.startsWith('/unread-conversations-count');
}

/**
 * Invalidée après chaque marquage lu/non lu (`marquerConversationLue` /
 * `marquerConversationNonLue`) : le compteur porte des CONVERSATIONS, pas des
 * messages, et corriger ce nombre en local demanderait de savoir si la
 * conversation touchée comptait déjà pour un autre agent connecté — un aller-
 * retour serveur, pas une déduction.
 *
 * Le changement d'établissement, lui, n'a rien à invalider : `facility_id` est
 * dans l'URL donc dans la clé, la bascule interroge déjà une clé différente.
 */
export function invaliderNombreConversationsNonLues(queryClient: QueryClient): void {
    void queryClient.invalidateQueries({ predicate: estRequeteNombreConversationsNonLues, refetchActive: true });
}

/**
 * Sondé au même rythme que le fil vivant (#83, `useFilConversation.ts`) : 30 s,
 * jamais en arrière-plan — un chiffre qui traîne pendant qu'on ne regarde pas
 * l'app ne coûte rien, le maintenir à jour coûterait une requête par onglet caché.
 */
const SONDAGE = {
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
};

/** Le nombre de conversations non lues sur l'établissement sélectionné, ou 0. */
export function useNombreConversationsNonLues(): number {
    const { currentFacility } = useStateContext();
    const { hasPermission } = usePermissions();

    // Même gating que l'écran « Messagerie » du registre (#17 §5) : pas de
    // permission, pas de requête — pas seulement pas d'affichage.
    const eligible = hasPermission('messaging.view');
    const url = eligible ? urlNombreConversationsNonLues(currentFacility?.id ?? null) : '';

    const { data } = useApiQuery<NombreConversationsNonLues>(url, {}, { ...SONDAGE, enabled: url !== '' });

    return data?.unread_conversations_count ?? 0;
}
